/* eslint-disable no-console */
import { BlockSize } from 'aos-helpers/src/helpers/Block'
import { Color } from 'aos-ui-common/src/styles/Color'
import { iconFor, tenantIcon } from 'aos-ui-universal'
import { compiledIcon, type IconName } from 'aos-ui-universal/src/icons/compiled'
import React, { CSSProperties, SVGAttributes } from 'react'
import styled, { CSSObject } from 'styled-components'

import { MarginBoxProps, marginStyleGenerator } from '../base/Box'
import { SvgProps } from '../base/SvgProps'
import { SvgIcon } from './SvgIcon'
import { SvgImage } from './SvgImage'

export interface IconProps extends Partial<SvgProps>, MarginBoxProps {
    iconName?: IconName
    svg?: any
    iconSize?: BlockSize
    iconVariant?: IconVariant
    block?: boolean
    raw?: boolean
    transparent?: boolean
    color?: Color
    bgColor?: Color
    outline?: boolean
    style?: CSSProperties
    attributes?: SVGAttributes<any>
}

export enum IconVariant {
    White = 1,
    Black,
    BlackGrey,
    Grey,
    Red,
    Blue,
    GreyTxt,
    Grey2,
    Yellow,
    Green,
    MenuButton,
    Smart,
    WidgetPrimary,
}

export const variantColors: Record<IconVariant, Color> = {
    [IconVariant.White]: Color.White,
    [IconVariant.Black]: Color.ContentOnGraphics,
    [IconVariant.BlackGrey]: Color.SideMenuInactive,
    [IconVariant.Grey]: Color.Grey2,
    // Redesign (AOS-843/844): the semantic status variants use the universal
    // status tokens, so trend arrows / dismiss / validation glyphs match the
    // status dots and borders instead of the legacy hexes.
    [IconVariant.Red]: Color.StatusCritical,
    [IconVariant.Blue]: Color.Primary,
    [IconVariant.GreyTxt]: Color.WidgetSecondary,
    [IconVariant.WidgetPrimary]: Color.WidgetPrimary,
    [IconVariant.Yellow]: Color.StatusWarning,
    [IconVariant.Green]: Color.StatusSuccess,
    [IconVariant.Grey2]: Color.ChartBase,
    [IconVariant.MenuButton]: Color.MenuButtonColor,
    [IconVariant.Smart]: Color.TextTheme,
}

const StyledSvgImage = styled(SvgImage)<Omit<IconProps, 'iconName' | 'svg'>>(({
    iconSize,
    iconVariant,
    block,
    transparent,
    raw = false,
    color,
    outline,
    bgColor,
    ...props
}) => {
    const styles: CSSObject = {
        display: 'inline-block',
        fill: 'currentColor',
        // An icon never shrinks. This used to be set only alongside an explicit
        // `iconSize`, so a sized icon held its box while an unsized one — the
        // common case in a row next to a label — was squeezed by a long title
        // wrapping onto more lines.
        flexShrink: 0,
    }

    const margins = marginStyleGenerator(props)

    if (iconSize) {
        styles.width = `${iconSize}px`
        styles.height = `${iconSize}px`
    }

    if (block) {
        styles.display = 'block'
    }

    if (iconVariant) {
        styles.fill = variantColors[iconVariant]
    }

    if (transparent) {
        styles.fill = 'transparent'
    }

    if (color) {
        styles.fill = color
    }

    // Handle rect-specific styling for checkboxes (border and background)
    // Exclude elements with .raw class to preserve their original colors
    if (outline) {
        styles.stroke = color
        styles.fill = bgColor || 'transparent'
        // For rect elements, explicitly set bgColor as fill and color as stroke
        // This overrides any inherit settings from !raw
        styles['rect:not(.raw)'] = {
            fill: bgColor || 'transparent',
            stroke: color,
        }
    } else if (bgColor) {
        // When bgColor is provided without outline, apply it to rect elements (for checkbox backgrounds)
        styles['rect:not(.raw)'] = {
            fill: bgColor,
        }
        if (color) {
            styles['rect:not(.raw)'] = {
                ...(styles['rect:not(.raw)'] as CSSObject),
                stroke: color,
            }
        }
    } else if (color && !transparent && !outline) {
        // When only color is provided, apply to both fill and stroke for rect elements
        styles['rect:not(.raw)'] = {
            fill: color,
            stroke: color,
        }
    }

    // Apply inherit fill to child elements (but rect styles above will override for rects)
    // Exclude elements with .raw class to preserve their original colors
    if (!raw) {
        styles['path:not(.raw),rect:not(.raw),g:not(.raw),circle:not(.raw)'] = {
            fill: 'inherit',
        }
    }

    return { ...styles, ...margins }
})

export const Icon: React.FC<IconProps> = ({ iconName, svg, ...props }) => {
    const resolvedSvg = iconName ? iconFor(iconName) : svg

    if (!resolvedSvg) {
        console.warn(`Icon not found: ${iconName || 'svg prop missing'}`)
        return null
    }

    return <StyledSvgImage svg={resolvedSvg} {...props} />
}

/**
 * Tenant artwork from runtime config wins over the compiled glyph of the same name. `name` is
 * widened from SvgIconType because config-supplied names are not knowable at compile time.
 */
export const svgIconForName = (
    name: SvgIconType | (string & Record<never, never>),
    fallback: (typeof SvgIcon)[SvgIconType] = SvgIcon.Default,
) => {
    // Config keys are strings, so a numeric or symbol key cannot have tenant artwork behind it.
    const tenant = typeof name === 'string' ? tenantIcon(name) : undefined
    return (
        tenant ??
        (Object.prototype.hasOwnProperty.call(SvgIcon, name)
            ? SvgIcon[name as SvgIconType]
            : undefined) ??
        compiledIcon(name as IconName) ??
        fallback
    )
}

export type SvgIconType = keyof typeof SvgIcon
